export type DetectedFileType =
  | "jpg"
  | "png"
  | "webp"
  | "gif"
  | "mp4"
  | "webm"
  | "unknown";

export interface FileTypeDetectionResult {
  fileType: DetectedFileType;
  isValid: boolean;
  isImage: boolean;
  isVideo: boolean;
}

const IMAGE_TYPES: DetectedFileType[] = ["jpg", "png", "webp", "gif"];
const VIDEO_TYPES: DetectedFileType[] = ["mp4", "webm"];

/**
 * Detects file type from either:
 * - a normal URL (based on filename extension)
 * - a blob URL (based on blob MIME type)
 */
export const detectCTAVisualFileType = async (
  src: string
): Promise<FileTypeDetectionResult> => {
  let fileType: DetectedFileType = "unknown";

  // Blob URLs do not contain filenames, so we inspect the MIME type.
  if (src.startsWith("blob:")) {
    try {
      const blob = await fetch(src).then((res) => res.blob());
      fileType = detectFromMime(blob.type);
    } catch {
      fileType = "unknown";
    }
  } else {
    try {
      // Remove query params
      const { pathname } = new URL(src);

      const ext = pathname.split(".").pop()?.toLowerCase();

      switch (ext) {
        case "jpg":
        case "jpeg":
          fileType = "jpg";
          break;
        case "png":
          fileType = "png";
          break;
        case "webp":
          fileType = "webp";
          break;
        case "gif":
          fileType = "gif";
          break;
        case "mp4":
          fileType = "mp4";
          break;
        case "webm":
          fileType = "webm";
          break;
        default:
          fileType = "unknown";
          break;
      }
    } catch {
      fileType = "unknown";
    }
  }

  return {
    fileType,
    isValid: fileType !== "unknown",
    isImage: IMAGE_TYPES.includes(fileType),
    isVideo: VIDEO_TYPES.includes(fileType),
  };
};

const detectFromMime = (mime?: string | null): DetectedFileType => {
  if (!mime) {
    return "unknown";
  }

  const normalized = mime.split(";")[0].toLowerCase();

  switch (normalized) {
    case "image/jpeg":
      return "jpg";
    case "image/png":
      return "png";
    case "image/webp":
      return "webp";
    case "image/gif":
      return "gif";
    case "video/mp4":
      return "mp4";
    case "video/webm":
      return "webm";
    default:
      return "unknown";
  }
};
